import { type CSSProperties } from 'react';

import { cn } from 'src/lib/utils';
import { useAnimatedNumber } from 'src/hooks';

import { ASSURANCE_LEVELS, type IAssuranceLevel, type IAssuranceSummary } from '../model';

// Keeps a tiny non-zero share visible
const MIN_FILL_PX = 16;

const getFillOffset = (percentage: number) =>
  percentage > 0 ? `min(${100 - percentage}%, calc(100% - ${MIN_FILL_PX}px))` : '100%';

type IBarProps = {
  level: IAssuranceLevel;
  summary: IAssuranceSummary;
};

const AssuranceBar = ({ level, summary }: IBarProps) => {
  const { label, icon: Icon, barClassName, eligibleFields, eligibleNoun } = level;
  const count = summary[level.countField];
  const eligibleCount = eligibleFields.reduce((sum, field) => sum + summary[field], 0);
  const targetPercentage = summary[level.percentageField];
  const percentage = useAnimatedNumber(targetPercentage);

  return (
    <li aria-label={label} className="flex flex-col items-center gap-2 text-center">
      <span className="text-lg font-extrabold tabular-nums text-foreground">{Math.round(percentage)}%</span>
      <div aria-hidden="true" className="relative h-50 w-14 overflow-hidden rounded-lg bg-muted">
        <div
          className={cn(
            'absolute inset-0 rounded-lg translate-y-(--fill-offset) transition-transform duration-500 ease starting:translate-y-full motion-reduce:transition-none',
            barClassName,
          )}
          style={{ '--fill-offset': getFillOffset(targetPercentage) } as CSSProperties}
        />
      </div>
      <Icon aria-hidden="true" className="size-6" />
      <span className="text-xs text-muted-foreground">{`${count} of ${eligibleCount} ${eligibleNoun}`}</span>
    </li>
  );
};

type Props = {
  summary: IAssuranceSummary;
};

const AssuranceBars = ({ summary }: Props) => (
  <section className="space-y-4">
    <h3 className="text-2xs font-extrabold uppercase tracking-[0.06em] text-muted-foreground">
      Active certificates per verification level
    </h3>
    <ul aria-label="Active certificates per verification level" className="grid grid-cols-3 gap-2">
      {ASSURANCE_LEVELS.map((level) => (
        <AssuranceBar key={level.key} level={level} summary={summary} />
      ))}
    </ul>
  </section>
);

export default AssuranceBars;
